import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthError } from '@/components/auth/AuthError';
import { AuthSuccess } from '@/components/auth/AuthSuccess';
import { SubmitButton } from '@/components/auth/SubmitButton';
import { supabase } from '@/lib/supabase';
import { describeAuthError } from '@/lib/authErrors';
import { useAuthStore } from '@/stores/auth';

type Status = 'verifying' | 'success' | 'error' | 'awaiting';
type ResendState = 'idle' | 'sending' | 'sent' | 'error';

export default function EmailConfirmationPage(): JSX.Element {
  const [params] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  async function handleResend(): Promise<void> {
    if (!user?.email) return;
    setResendState('sending');
    setResendError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: { emailRedirectTo: `${window.location.origin}/confirm-email` },
      });
      if (error) {
        setResendState('error');
        setResendError(describeAuthError(error));
        return;
      }
      setResendState('sent');
    } catch (err) {
      setResendState('error');
      setResendError(describeAuthError(err));
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function verify(): Promise<void> {
      // Modern Supabase email links use ?code=... (PKCE) or land here with
      // tokens already in the URL hash (handled by detectSessionInUrl).
      // Older / magic-link variants may carry ?error_description=...
      const errorDescription = params.get('error_description');
      if (errorDescription) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(errorDescription);
        }
        return;
      }

      const code = params.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (error) {
          setStatus('error');
          setErrorMessage(error.message);
          return;
        }
        setStatus('success');
        return;
      }

      // No code in URL: either the user clicked a link that already set a
      // session via the URL hash (in which case the auth listener fires and
      // we land here authenticated), or they navigated here on their own
      // while not yet confirmed.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      const sessionUser = data.session?.user;
      if (sessionUser?.email_confirmed_at) {
        setStatus('success');
        return;
      }

      setStatus('awaiting');
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [params]);

  if (status === 'verifying') {
    return (
      <AuthLayout title="Confirming your email">
        <p className="text-sm text-ink-mid">Hold fast…</p>
      </AuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <AuthLayout
        title="Email confirmed"
        subtitle="Welcome aboard."
        footer={
          <Link to="/login" className="text-ink underline hover:text-candle-dark">
            Sign in to continue
          </Link>
        }
      >
        <AuthSuccess>Your email is verified. You can sign in now.</AuthSuccess>
      </AuthLayout>
    );
  }

  if (status === 'error') {
    return (
      <AuthLayout
        title="Confirmation failed"
        footer={
          <Link to="/signup" className="text-ink underline hover:text-candle-dark">
            Back to signup
          </Link>
        }
      >
        <AuthError message={errorMessage ?? 'The confirmation link is invalid or has expired.'} />
        <p className="text-sm text-ink-mid leading-relaxed">
          Confirmation links expire after 24 hours. You can request a new one by signing up again
          with the same email.
        </p>
      </AuthLayout>
    );
  }

  // awaiting
  const canResend = Boolean(user?.email) && resendState !== 'sent';
  return (
    <AuthLayout
      title="Email not confirmed yet"
      footer={
        <Link to="/login" className="text-ink underline hover:text-candle-dark">
          Back to sign in
        </Link>
      }
    >
      <p className="text-sm text-ink-mid leading-relaxed mb-4">
        {user?.email
          ? `We're still waiting for ${user.email} to confirm. Open the confirmation link in your inbox to finish enlisting.`
          : 'Open the confirmation link we sent you to finish enlisting.'}
      </p>

      {resendState === 'sent' && (
        <AuthSuccess>
          Sent. Check your inbox in a minute. If you still do not see it, peek in spam.
        </AuthSuccess>
      )}
      {resendState === 'error' && <AuthError message={resendError} />}

      {canResend && (
        <SubmitButton
          type="button"
          loading={resendState === 'sending'}
          disabled={!user?.email}
          onClick={() => {
            void handleResend();
          }}
        >
          {resendState === 'sending' ? 'Sending…' : 'Resend confirmation email'}
        </SubmitButton>
      )}

      {!user?.email && (
        <p className="text-xs text-ink-light mt-3">
          Sign in first to request a new confirmation email.
        </p>
      )}
    </AuthLayout>
  );
}
